import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, API } from "@/lib/api";
import { authHeaders, setSessionToken } from "@/lib/session";
import { useUser } from "@/store/user";
import usePageTitle from "@/hooks/usePageTitle";
import { openManageSubscription, resolveManageSubscription, isNativeApp } from "@/lib/platform";
import * as RC from "@/lib/revenuecat";
const LESSON_BADGES = [
  { id: "simile_master", name: "سيّد التشبيه", icon: "🌿" },
  { id: "metaphor_master", name: "صانع الصور", icon: "🔥" },
  { id: "rhythm_keeper", name: "حارس الإيقاع", icon: "🎵" },
  { id: "concise_writer", name: "كاتب الإيجاز", icon: "✒️" },
  { id: "heart_voice", name: "صوت القلب", icon: "💜" },
  { id: "comparison_master", name: "سيّد المقارنة", icon: "⚖️" },
  { id: "question_crafter", name: "صانع الأسئلة", icon: "❓" },
  { id: "voice_of_impact", name: "صوت التأثير", icon: "🔊" },
  { id: "kinaya_master", name: "سيّد الكناية", icon: "🌙" },
  { id: "rising_orator", name: "الخطيب الصاعد", icon: "🎤" },
];

export default function ProfilePage() {
  const navigate = useNavigate();
  usePageTitle("حسابي · بَلاغة");
  const { user, isAuthed, progress, signOut, subInfo, refreshSubscription } = useUser();
  const [lessons, setLessons] = useState([]);
  const [isAdmin, setIsAdmin] = useState(false);
  const [cancelBusy, setCancelBusy] = useState(false);
  const [showMoyasarCancel, setShowMoyasarCancel] = useState(false);
  const sub = subInfo?.subscription;
  // Resolve subscription-management action from provider (not device).
  // Button is hidden entirely when there is no active subscription.
  const manageAction = sub ? resolveManageSubscription(sub) : { kind: "none" };

  useEffect(() => {
    api.listLessons().then(setLessons).catch(() => {});
  }, []);

  useEffect(() => {
    if (!isAuthed) { setIsAdmin(false); return; }
    fetch(`${API}/admin/me`, { credentials: "include", headers: authHeaders() })
      .then((r) => r.json())
      .then((d) => setIsAdmin(!!d.is_admin))
      .catch(() => setIsAdmin(false));
  }, [isAuthed, user]);

  const earned = new Set((progress.badges || []).map((b) => b.id));
  const completedLessons = progress.completed_lessons?.length || 0;
  const completedChallenges = progress.completed_challenges?.length || 0;
  const initial = (user?.name || "ز")[0];

  return (
    <div className="shell has-bottom-nav" data-testid="profile-page">
      <div className="page-header">
        <h1>
          ملفّي في <span className="gold">بَلاغة</span>
        </h1>
        <p>تابع تقدّمك، استعرض شاراتك، وأدر حسابك من هنا.</p>
      </div>

      <div className="profile-hero" data-testid="profile-hero">
        <div className="profile-avatar">
          {isAuthed && user?.picture ? <img src={user.picture} alt={user.name} /> : initial}
        </div>
        <div className="profile-info" style={{ flex: 1 }}>
          <h2 data-testid="profile-name">
            {isAuthed ? user.name : "زائر"}
          </h2>
          <div className="em" data-testid="profile-email">
            {isAuthed ? user.email : "سجّل دخولك لحفظ تقدّمك عبر الأجهزة"}
          </div>
          <div className="row" style={{ marginTop: 12 }}>
            {!isAuthed ? (
              <button
                className="btn btn-gold"
                onClick={() => navigate("/login")}
                data-testid="profile-login-btn"
                style={{ padding: "10px 16px", fontSize: 14 }}
              >
                تسجيل الدخول بـ Google
              </button>
            ) : (
              <button
                className="btn btn-outline"
                onClick={signOut}
                data-testid="profile-logout-btn"
                style={{ padding: "10px 16px", fontSize: 14 }}
              >
                تسجيل الخروج
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="stat-grid" data-testid="profile-stats">
        <div className="stat-tile">
          <div className="v" data-testid="stat-tile-xp">{progress.xp}</div>
          <div className="l">⭐ نقاط الخبرة</div>
        </div>
        <div className="stat-tile">
          <div className="v" data-testid="stat-tile-streak">{progress.streak}</div>
          <div className="l">🔥 سلسلة يوميّة</div>
        </div>
        <div className="stat-tile">
          <div className="v" data-testid="stat-tile-badges">{progress.badges?.length || 0}</div>
          <div className="l">🏅 شارات</div>
        </div>
      </div>

      <div className="stat-grid" style={{ gridTemplateColumns: "1fr 1fr" }}>
        <div className="stat-tile">
          <div className="v">{completedLessons}/70</div>
          <div className="l">📚 دروس مكتملة</div>
        </div>
        <div className="stat-tile">
          <div className="v">{completedChallenges}</div>
          <div className="l">🏆 تحدّيات مكتملة</div>
        </div>
      </div>

      <h2 className="section-title">اشتراكي</h2>
      <div
        className="panel"
        data-testid="profile-subscription"
        style={{
          padding: 18,
          marginBottom: 18,
          border: sub?.active
            ? "1px solid #2ec48e55"
            : "1px solid #2a2640",
          background: sub?.active
            ? "rgba(46,196,142,0.06)"
            : "transparent",
        }}
      >
        {!isAuthed ? (
          <div className="muted" style={{ fontSize: 14, lineHeight: 1.8 }}>
            سجّل الدخول لإدارة اشتراكك واستعراض حالة Premium.
          </div>
        ) : sub?.active ? (
          <div>
            <div
              style={{
                display: "flex",
                gap: 10,
                alignItems: "center",
                marginBottom: 8,
                flexWrap: "wrap",
              }}
            >
              <strong style={{ color: "#2ec48e", fontSize: 16 }}>
                {sub.plan === "yearly"
                  ? "💎 Premium — سنويّ"
                  : sub.plan === "monthly"
                  ? "💎 Premium — شهريّ"
                  : "💎 Premium نشط"}
              </strong>
              {sub.cancelled && (
                <span style={{ color: "#f5c87a", fontSize: 13 }}>
                  (سيُلغى عند انتهاء الفترة)
                </span>
              )}
            </div>
            <div className="muted" style={{ fontSize: 13, lineHeight: 1.8 }}>
              {sub.expires_at && (
                <>
                  ينتهي في:{" "}
                  <strong>
                    {new Date(sub.expires_at).toLocaleDateString("ar-EG", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </strong>{" "}
                  ({sub.days_remaining} يوم متبقّي)
                </>
              )}
            </div>
            <div style={{ display: "flex", gap: 10, marginTop: 14, flexWrap: "wrap" }}>
              {!sub.cancelled && (
                <button
                  className="btn btn-ghost"
                  data-testid="profile-cancel-sub"
                  disabled={cancelBusy}
                  onClick={async () => {
                    if (!window.confirm("هل تريد إلغاء التجديد التلقائيّ؟ ستبقى متاحًا حتى نهاية الفترة.")) return;
                    setCancelBusy(true);
                    try {
                      await api.cancelSubscription();
                      await refreshSubscription();
                    } catch (_) {}
                    setCancelBusy(false);
                  }}
                  style={{ padding: "8px 14px", fontSize: 13 }}
                >
                  إلغاء التجديد
                </button>
              )}
              {sub.trial && (
                <button
                  className="btn btn-gold"
                  onClick={() => navigate("/pricing")}
                  data-testid="profile-upgrade-from-trial"
                  style={{
                    background: "#c79c6a",
                    color: "#1a1530",
                    padding: "8px 14px",
                    fontSize: 13,
                    fontWeight: 700,
                    display: "none",  /* Trial removed per store submission decision */
                  }}
                >
                  انتقل إلى اشتراك مدفوع
                </button>
              )}
            </div>
          </div>
        ) : (
          <div>
            <div style={{ fontSize: 15, marginBottom: 6 }}>
              أنت على الخطّة <strong>المجّانيّة</strong>.
            </div>
            <div className="muted" style={{ fontSize: 13, lineHeight: 1.8, marginBottom: 14 }}>
              مسار «بَلاغة 1» مفتوحٌ بالكامل. افتح بقيّة المسارات المدفوعة
              باشتراكٍ يبدأ من 29 ر.س / شهر عبر App Store أو Google Play.
            </div>
            <button
              className="btn btn-gold"
              onClick={() => navigate("/pricing")}
              data-testid="profile-go-pricing"
              style={{
                background: "#c79c6a",
                color: "#1a1530",
                padding: "10px 18px",
                fontSize: 14,
                fontWeight: 700,
              }}
            >
              🚀 ترقية إلى Premium
            </button>
          </div>
        )}
      </div>

      <h2 className="section-title">شاراتك</h2>
      <div className="badge-grid" data-testid="profile-badges">
        {LESSON_BADGES.map((b) => {
          const got = earned.has(b.id);
          return (
            <div
              key={b.id}
              className={`badge-tile ${got ? "earned" : "locked"}`}
              data-testid={`badge-tile-${b.id}`}
              title={got ? b.name : `أكمل الدرس لكسب: ${b.name}`}
            >
              <div className="ic">{b.icon}</div>
              <div className="nm">{b.name}</div>
            </div>
          );
        })}
      </div>

      <div style={{ marginTop: 32 }}>
        <h2 className="section-title">روابط سريعة</h2>
        <div className="row" style={{ gap: 10 }}>
          <button
            className="btn btn-ghost"
            onClick={() => navigate("/")}
            data-testid="quick-link-home"
          >
            🏠 الصفحة الرئيسية
          </button>
          <button
            className="btn btn-ghost"
            onClick={() => navigate("/chat")}
            data-testid="quick-link-chat"
          >
            💬 تحدّث مع المعلّم
          </button>
          <button
            className="btn btn-ghost"
            onClick={() => navigate("/challenges")}
            data-testid="quick-link-challenges"
          >
            🏆 التحدّيات
          </button>
          <button
            className="btn btn-ghost"
            onClick={() => navigate("/about")}
            data-testid="quick-link-about"
          >
            ℹ️ عن المشروع
          </button>
          {isAdmin && (
            <button
              className="btn btn-gold"
              onClick={() => navigate("/admin")}
              data-testid="quick-link-admin"
            >
              🛠️ لوحة الإدارة
            </button>
          )}
        </div>
      </div>

      {/* Subscription management — required by App Store + Play Store */}
      {isAuthed && (
        <div
          data-testid="profile-subscription-mgmt"
          style={{
            marginTop: 24,
            padding: 18,
            border: "1px solid #2a2640",
            borderRadius: 14,
            background: "rgba(199,156,106,0.05)",
          }}
        >
          <h3 style={{ marginBottom: 6, fontSize: 18, color: "#c79c6a" }}>
            💳 الاشتراك
          </h3>
          <p
            className="muted"
            style={{ marginBottom: 12, fontSize: 13, lineHeight: 1.85 }}
          >
            {sub?.active
              ? (() => {
                  const provider = (sub.provider || "").toLowerCase();
                  const plan = sub?.plan ? ` (${sub.plan})` : "";
                  if (provider === "apple")
                    return `اشتراكك نشط${plan} عبر App Store. أدِر الخطّة أو ألغِ التجديد التلقائيّ من إعدادات Apple ID.`;
                  if (provider === "google")
                    return `اشتراكك نشط${plan} عبر Google Play. أدِر الخطّة أو ألغِ التجديد التلقائيّ من Play Store.`;
                  if (provider === "moyasar")
                    return `اشتراكك نشط${plan} عبر الدفع الإلكترونيّ (Moyasar). يمكنك إلغاء التجديد التلقائيّ من هنا.`;
                  if (provider === "revenuecat")
                    return `اشتراكك نشط${plan} عبر متجر الهاتف. أدِر الخطّة من إعدادات App Store أو Google Play.`;
                  return `اشتراكك نشط${plan}. تواصل مع الدعم للإدارة.`;
                })()
              : "لا يوجد اشتراك نشط حاليًّا. عند الاشتراك، ستظهر خيارات الإدارة هنا."}
          </p>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
            {manageAction.kind !== "none" && (
              <button
                type="button"
                className="btn btn-primary"
                data-testid="profile-manage-subscription"
                data-manage-kind={manageAction.kind}
                data-provider={(sub?.provider || "unknown").toLowerCase()}
                onClick={() => {
                  if (manageAction.kind === "in_app_cancel") {
                    setShowMoyasarCancel(true);
                  } else {
                    openManageSubscription(sub);
                  }
                }}
                style={{ padding: "10px 18px" }}
              >
                ⚙️ إدارة الاشتراك
              </button>
            )}
            <button
              type="button"
              className="btn btn-ghost"
              data-testid="profile-restore-purchases"
              onClick={async () => {
                setCancelBusy(true);
                try {
                  if (isNativeApp) {
                    await RC.init(user?.id || null);
                    await RC.restore();
                  }
                  if (typeof api.restorePurchases === "function") {
                    await api.restorePurchases();
                  }
                  await refreshSubscription();
                  window.alert(
                    "تمّت مزامنة حالة اشتراكك مع المتجر. إن لم يظهر اشتراكك، تأكّد من تسجيل الدخول بنفس حساب Apple/Google الذي اشتريت به."
                  );
                } catch (e) {
                  window.alert(
                    e?.response?.data?.detail || e?.message || "تعذّر استرجاع المشتريات."
                  );
                } finally {
                  setCancelBusy(false);
                }
              }}
              disabled={cancelBusy}
              style={{ padding: "10px 18px" }}
            >
              🔄 استرجاع المشتريات
            </button>
          </div>
        </div>
      )}

      {/* Moyasar in-app cancel confirmation (web / self-service only) */}
      {showMoyasarCancel && (
        <div
          data-testid="moyasar-cancel-modal"
          role="dialog"
          aria-modal="true"
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.65)",
            display: "flex", alignItems: "center", justifyContent: "center",
            padding: 20, zIndex: 1000,
          }}
          onClick={() => !cancelBusy && setShowMoyasarCancel(false)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: "#1a1730", padding: 24, borderRadius: 16,
              maxWidth: 440, width: "100%", border: "1px solid #ff7aa855",
            }}
          >
            <h3 style={{ color: "#ff7aa8", marginBottom: 10, fontSize: 18 }}>
              إلغاء التجديد التلقائيّ
            </h3>
            <p className="muted" style={{ marginBottom: 20, lineHeight: 1.9, fontSize: 14 }}>
              سيبقى اشتراكك نشطًا حتى {sub?.expires_at ? new Date(sub.expires_at).toLocaleDateString("ar-SA") : "نهاية الفترة المدفوعة"}، ثمّ لن يُجدَّد تلقائيًّا. هل تريد المتابعة؟
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button
                type="button"
                className="btn btn-ghost"
                data-testid="moyasar-cancel-dismiss"
                onClick={() => setShowMoyasarCancel(false)}
                disabled={cancelBusy}
              >
                تراجع
              </button>
              <button
                type="button"
                className="btn btn-primary"
                data-testid="moyasar-cancel-confirm"
                disabled={cancelBusy}
                onClick={async () => {
                  setCancelBusy(true);
                  try {
                    await api.cancelSubscription();
                    await refreshSubscription();
                    setShowMoyasarCancel(false);
                    window.alert("تمّ إلغاء التجديد التلقائيّ. سيبقى اشتراكك نشطًا حتى نهاية الفترة المدفوعة.");
                  } catch (e) {
                    window.alert(e?.response?.data?.detail || e?.message || "تعذّر الإلغاء — تواصل مع الدعم.");
                  } finally {
                    setCancelBusy(false);
                  }
                }}
              >
                نعم، ألغِ التجديد
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Danger zone — required by Apple App Store + Google Play */}
      {isAuthed && (
        <div
          data-testid="profile-danger-zone"
          style={{
            marginTop: 36,
            padding: 18,
            border: "1px solid #ff7aa855",
            borderRadius: 14,
            background: "rgba(255,122,168,0.04)",
          }}
        >
          <h3 style={{ color: "#ff7aa8", marginBottom: 6, fontSize: 18 }}>
            حذف الحساب
          </h3>
          <p
            className="muted"
            style={{ marginBottom: 14, fontSize: 13, lineHeight: 1.85 }}
          >
            حذف حسابك يَمحو كلَّ بياناتك نهائيًّا: التقدُّم، الشارات،
            الشهادات، الاشتراك، والمحادثات. لا يمكن التراجع عن هذه العمليّة.
          </p>
          <button
            className="btn"
            data-testid="profile-delete-account"
            onClick={async () => {
              const c1 = window.confirm(
                "هل أنت متأكّد من حذف حسابك؟ هذه العمليّة لا رجعة فيها."
              );
              if (!c1) return;
              const c2 = window.prompt(
                "للتأكيد، اكتب كلمة «حذف» ثم اضغط موافق:"
              );
              if (c2 !== "حذف") {
                alert("ألغيت العمليّة.");
                return;
              }
              try {
                await api.deleteAccount();
                await setSessionToken(null);
                alert("تمّ حذف حسابك بنجاح. وداعًا 👋");
                localStorage.removeItem("balagha_uid");
                window.location.href = "/";
              } catch (e) {
                alert(
                  e?.response?.data?.detail ||
                    "تعذّر حذف الحساب. تواصل معنا إن استمرّت المشكلة."
                );
              }
            }}
            style={{
              background: "rgba(255,122,168,0.18)",
              color: "#ff7aa8",
              border: "1px solid #ff7aa888",
              padding: "10px 18px",
              fontWeight: 600,
            }}
          >
            🗑️ حذف حسابي نهائيًّا
          </button>
        </div>
      )}
    </div>
  );
}
